import { useState } from 'react';
import type { PeriodStats, ThresholdStats } from '../hooks/useAnalytics';

interface AnalyticsOverviewProps {
  periodStats: PeriodStats | null;
  thresholdStats: ThresholdStats | null;
  selectedPeriod: string;
  onPeriodChange: (period: 'weekly' | 'monthly' | 'quarterly' | 'six_month' | 'annual') => void;
}

export function AnalyticsOverview({
  periodStats,
  thresholdStats,
  selectedPeriod,
  onPeriodChange
}: AnalyticsOverviewProps) {
  const [tooltip, setTooltip] = useState<{
    content: string;
    x: number;
    y: number;
    visible: boolean;
  } | null>(null);

  const formatNumber = (num: number, decimals: number = 1) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(decimals)}k`;
    }
    return num.toFixed(decimals);
  };

  const formatLargeNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}k`;
    }
    return Math.round(num).toString();
  };

  const showTooltip = (content: string, event: React.MouseEvent) => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    setTooltip({
      content,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
      visible: true
    });
  };

  const hideTooltip = () => {
    setTooltip(null);
  };

  const getTooltipContent = (type: string) => {
    switch (type) {
      case 'days':
        return "Total number of days analyzed in this time period. More days = more reliable statistics.";
      case 'performance':
        return "Average performance ratio (actual vs expected output). 100% = perfect performance. Shows how well the solar farm performs against theoretical maximum.";
      case 'generation':
        return "Total megawatt-hours (MWh) generated across the entire period. This is the raw energy production before any losses.";
      case 'deficit':
        return "Energy shortfall in MWh. Difference between expected and actual generation. Shows how much energy was 'lost' to inefficiency, weather, and system failures.";
      case 'capacity':
        return "Capacity factor - percentage of theoretical maximum output actually achieved. UK solar average is ~10.8%. Higher is better, but solar rarely exceeds 15-20%.";
      case 'homes':
        return "Equivalent number of UK homes powered per day (based on 11 kWh average daily consumption). Shows the real-world impact of this solar farm's output.";
      default:
        return "";
    }
  };

  return (
    <div className="analytics-overview">
      {/* Tooltip */}
      {tooltip && tooltip.visible && (
        <div
          className="analytics-tooltip"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            position: 'fixed',
            transform: 'translate(-50%, -100%)',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '0.85em',
            maxWidth: '250px',
            zIndex: 1000,
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            whiteSpace: 'normal'
          }}
        >
          {tooltip.content}
        </div>
      )}
      {/* Period Selector */}
      <div className="period-selector">
        <h3>Analysis Period</h3>
        <div className="period-buttons">
          {[
            { key: 'weekly' as const, label: 'Weekly' },
            { key: 'monthly' as const, label: 'Monthly' },
            { key: 'quarterly' as const, label: 'Quarterly' },
            { key: 'six_month' as const, label: '6 Months' },
            { key: 'annual' as const, label: 'Annual' }
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`period-btn ${selectedPeriod === key ? 'active' : ''}`}
              onClick={() => onPeriodChange(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid">
        <div
          className="kpi-card"
          onMouseEnter={(e) => showTooltip(getTooltipContent('days'), e)}
          onMouseLeave={hideTooltip}
        >
          <div className="kpi-value">{periodStats?.total_days || 0}</div>
          <div className="kpi-label">Days Analyzed</div>
          <div className="kpi-subtitle">{periodStats?.start_date} to {periodStats?.end_date}</div>
        </div>

        <div
          className="kpi-card primary"
          onMouseEnter={(e) => showTooltip(getTooltipContent('performance'), e)}
          onMouseLeave={hideTooltip}
        >
          <div className="kpi-value">{((periodStats?.avg_ratio || 0) * 100).toFixed(1)}%</div>
          <div className="kpi-label">Avg Performance</div>
          <div className="kpi-subtitle">
            {formatNumber(periodStats?.avg_actual_mwh || 0)} vs {formatNumber(periodStats?.avg_expected_mwh || 0)} MWh/day
          </div>
        </div>

        <div
          className="kpi-card"
          onMouseEnter={(e) => showTooltip(getTooltipContent('generation'), e)}
          onMouseLeave={hideTooltip}
        >
          <div className="kpi-value">{formatLargeNumber(periodStats?.total_actual_mwh || 0)}</div>
          <div className="kpi-label">Total Generation</div>
          <div className="kpi-subtitle">MWh</div>
        </div>

        <div
          className="kpi-card warning"
          onMouseEnter={(e) => showTooltip(getTooltipContent('deficit'), e)}
          onMouseLeave={hideTooltip}
        >
          <div className="kpi-value">{formatLargeNumber(periodStats?.deficit_mwh || 0)}</div>
          <div className="kpi-label">Energy Deficit</div>
          <div className="kpi-subtitle">MWh shortfall</div>
        </div>

        <div
          className="kpi-card"
          onMouseEnter={(e) => showTooltip(getTooltipContent('capacity'), e)}
          onMouseLeave={hideTooltip}
        >
          <div className="kpi-value">{((periodStats?.avg_capacity_factor || 0) * 100).toFixed(1)}%</div>
          <div className="kpi-label">Capacity Factor</div>
          <div className="kpi-subtitle">Avg utilization</div>
        </div>

        <div
          className="kpi-card"
          onMouseEnter={(e) => showTooltip(getTooltipContent('homes'), e)}
          onMouseLeave={hideTooltip}
        >
          <div className="kpi-value">{formatLargeNumber(periodStats?.avg_homes_powered || 0)}</div>
          <div className="kpi-label">Homes Powered</div>
          <div className="kpi-subtitle">Avg per day</div>
        </div>
      </div>

      {/* Performance Insights */}
      <div className="insights-section">
        <h3>Performance Insights</h3>
        <div className="insights-grid">
          <div className="insight-card">
            <div className="insight-icon">⚡</div>
            <div className="insight-content">
              <div className="insight-value">{((periodStats?.avg_capacity_factor || 0) * 100).toFixed(1)}%</div>
              <div className="insight-label">Capacity Factor</div>
              <div className="insight-desc">
                {`❌ Capacity factor ${(periodStats?.avg_capacity_factor || 0) * 100}% vs UK average 10.8% and Europe avg ~12% – abysmal utilization. This solar farm is a complete waste of space.`}
              </div>
            </div>
          </div>

          <div className="insight-card">
            <div className="insight-icon">☁️</div>
            <div className="insight-content">
              <div className="insight-value">{(periodStats?.avg_cloud_cover || 0).toFixed(1)}%</div>
              <div className="insight-label">Avg Cloud Cover</div>
              <div className="insight-desc">
                {`☁️ ${(periodStats?.avg_cloud_cover || 0).toFixed(1)}% cloud cover punches this project in the gut. Clouds are the silent killer of solar dreams.`}
              </div>
            </div>
          </div>

          <div className="insight-card warning">
            <div className="insight-icon">⚠️</div>
            <div className="insight-content">
              <div className="insight-value">{thresholdStats?.below_threshold || 0}</div>
              <div className="insight-label">Days Below 50%</div>
              <div className="insight-desc">
                {`${thresholdStats?.below_threshold} days out of ${thresholdStats?.total_days} (${thresholdStats?.percentage.toFixed(1)}%) are worthless – this project fails ${(thresholdStats?.percentage || 0)}% of the time.`}
              </div>
            </div>
          </div>

          <div className="insight-card">
            <div className="insight-icon">🏠</div>
            <div className="insight-content">
              <div className="insight-value">{formatLargeNumber(periodStats?.avg_homes_powered || 0)}</div>
              <div className="insight-label">Daily Impact</div>
              <div className="insight-desc">
                Equivalent UK homes powered
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Threshold Analysis */}
      <div className="threshold-analysis">
        <h3>Performance Thresholds</h3>
        <div className="threshold-cards">
          <div className="threshold-card">
            <div className="threshold-header">
              <span className="threshold-dot safe"></span>
              <span>Above 70%</span>
            </div>
            <div className="threshold-value">
              {((thresholdStats?.total_days || 0) - (thresholdStats?.below_threshold || 0))} days
            </div>
            <div className="threshold-percent">
              {(((thresholdStats?.total_days || 0) - (thresholdStats?.below_threshold || 0)) / (thresholdStats?.total_days || 1) * 100).toFixed(1)}%
            </div>
          </div>

          <div className="threshold-card warning">
            <div className="threshold-header">
              <span className="threshold-dot danger"></span>
              <span>Below 70%</span>
            </div>
            <div className="threshold-value">
              {thresholdStats?.below_threshold || 0} days
            </div>
            <div className="threshold-percent">
              {thresholdStats?.percentage.toFixed(1)}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
